/**
 * Roblox Folder-Based Lua Template Renderer
 * Creates nested Lua tables based on folder hierarchy
 */

import { ExportTemplate, NamingConvention } from '../../../../types/exportTemplates';
import { applyNamingConvention } from '../../core/naming';
import { formatValue } from '../../core/formatting';
import { LookupContext } from '../../fieldMapping/references';
import { buildFolderBasedStructure, FolderNode, ItemFieldsNode } from '../../structure/folderBased';

/**
 * Render folder-based Lua template (Roblox)
 * Creates nested Lua tables based on folder hierarchy
 * 
 * @param items - Items to export
 * @param template - Export template configuration
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param types - Optional types data for reference resolution
 * @param tags - Optional tags data for reference resolution
 * @param allItemsForLookup - Optional all items for glossaryItem lookups
 * @param namingConventionOverride - Optional override for all naming conventions (e.g., 'original', 'camelCase')
 * @param customClassName - Optional custom name for the root table (default: 'Database')
 * @param dialogs - Optional dialogs array
 * @param documents - Optional documents array for document field resolution
 */
export function renderFolderBasedLuaTemplate(
  items: any[],
  template: ExportTemplate,
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[],
  namingConventionOverride?: NamingConvention,
  customClassName?: string,
  dialogs?: any[],
  documents?: any[]
): string {
  const lookupContext: LookupContext = {
    types,
    tags,
    allItems: allItemsForLookup || items,
    documents: documents
  };
  
  // Use override if provided, otherwise use template convention
  const fieldConvention = namingConventionOverride || template.namingConventions.fieldName;
  
  // Use custom class name or default to 'Database'
  const className = customClassName || 'Database';
  
  const structure = buildFolderBasedStructure(items, template, blueprints, folderPathResolver, lookupContext, namingConventionOverride);
  
  // Recursive function to render nested Lua tables
  const renderLuaNode = (node: FolderNode | ItemFieldsNode, indent: number = 1): string => {
    const tabs = '\t'.repeat(indent);
    let output = '';
    
    for (const key of Object.keys(node)) {
      // Skip internal properties
      if (key.startsWith('_')) continue;
      
      const value = node[key];
      
      if (value && typeof value === 'object' && '_isItem' in value) {
        // This is an item node - render its fields
        const itemNode = value as ItemFieldsNode;
        output += `${tabs}["${key}"] = {\n`;
        
        itemNode._fields.forEach(field => {
          let fieldName = applyNamingConvention(field.label, fieldConvention);
          
          // Check for field name override (only if not using a global override)
          if (!namingConventionOverride && template.fieldNameOverrides && itemNode._itemData.blueprint_id) {
            const blueprintOverrides = template.fieldNameOverrides[itemNode._itemData.blueprint_id];
            if (blueprintOverrides && blueprintOverrides[field.label]) {
              const override = blueprintOverrides[field.label];
              if (override.useExportName && override.exportName) {
                fieldName = override.exportName;
              }
            }
          }
          
          const fieldValue = field.value !== undefined 
            ? formatValue(field.value, field.mappedType, 'roblox')
            : field.defaultValue;
          
          output += `${tabs}\t["${fieldName}"] = ${fieldValue},\n`;
        });
        
        output += `${tabs}},\n`;
      } else if (value && typeof value === 'object') {
        // This is a folder node - recurse
        output += `${tabs}["${key}"] = {\n`;
        output += renderLuaNode(value as FolderNode, indent + 1);
        output += `${tabs}},\n`;
      }
    }
    
    return output;
  };
  
  let output = `-- Auto-generated ${className} (Folder Structure)\n`;
  output += '-- This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += `-- Access data like: ${className}.scenes.player_house.bed.inspect_text\n\n`;
  output += `local ${className} = {\n`;
  output += renderLuaNode(structure);
  output += `}\n\nreturn ${className}\n`;
  
  console.log('✅ Folder-based Lua template rendered successfully');
  
  return output;
}
